// src/services/pricingColumns.js - PRICING TABLE COLUMNS (CANONICAL VERSION)

import React from 'react';
import { dynastyTheme } from '../colorService';
import { renderDefault, renderFloat1, renderFloat2, renderFloat3, renderAvg } from './DynastyTable';
import InjuryBadge from '../../components/InjuryBadge';
import PlayerLink from '../../components/PlayerLink';
import { STAT_FIELD_MAP } from '../../utils/statMapping';
import { statFieldSimple } from '../../utils/statColumnDedup';

// =============================================================================
// PRICE PREVIEW COLUMNS - CANONICAL VERSION
// =============================================================================
export const createPricePreviewColumns = (params) => {
  const {
    bulkEditMode,
    selectedPlayers,
    setSelectedPlayers,
    handleManualPriceChange,
    settings,
    viewMode,
    leagueId,
    getFilteredPrices,
    selectedYear = 'current', // 'current' or 'previous'
    currentSeasonYear, // Actual year numbers for lookup
    previousSeasonYear
  } = params;

  // Determine which year to look up in stats.years array
  const targetYear = selectedYear === 'previous' ? previousSeasonYear : currentSeasonYear;
  const statSuffix = selectedYear === 'previous' ? '_prior' : '';

  const baseColumns = [];
  
  if (bulkEditMode) {
    baseColumns.push({
      key: 'select',
      title: (
        <input
          type="checkbox"
          checked={selectedPlayers.size === getFilteredPrices().length}
          onChange={(e) => {
            if (e.target.checked) {
              // CANONICAL: Access player ID from nested structure
              setSelectedPlayers(new Set(getFilteredPrices().map(p => p?.ids?.mlb)));
            } else {
              setSelectedPlayers(new Set());
            }
          }}
          className={dynastyTheme.classes.text.primary}
          style={{ accentColor: '#D4AF37' }}
        />
      ),
      width: 30,
      render: (player) => {
        const playerId = player?.ids?.mlb;
        return (
          <input
            type="checkbox"
            checked={selectedPlayers.has(playerId)}
            onChange={(e) => {
              const newSelected = new Set(selectedPlayers);
              if (e.target.checked) {
                newSelected.add(playerId);
              } else {
                newSelected.delete(playerId);
              }
              setSelectedPlayers(newSelected);
            }}
            className={dynastyTheme.classes.text.primary}
            style={{ accentColor: '#D4AF37' }}
          />
        );
      }
    });
  }
  
  baseColumns.push(
    {
      key: 'player_name',
      title: 'Name',
      width: 140,
      render: (player, rowType, item) => {
        const pr = player.pricing || {};
        const badges = [];
        if (pr.is_rookie) badges.push('🆕');
        if (pr.normalization_method === 'prior_year') badges.push('📊');
        if (pr.normalization_method === 'prior_year_reduced') badges.push('📅');
        if (pr.normalization_method === 'league_average') badges.push('📈');

        // CANONICAL: Access player ID from nested structure
        const playerId = player?.ids?.mlb;
        const playerName = player?.info?.full_name || 'Unknown Player';

        return (
          <div className="flex items-center gap-1">
            <PlayerLink mlbId={playerId} name={playerName} style={{ color: '#facc15', maxWidth: '110px' }} />
            <InjuryBadge mlbId={playerId} />
            {badges.length > 0 && (
              <span className="text-xs" title={`Normalization: ${pr.normalization_method}`}>
                {badges.join(' ')}
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: 'position',
      title: 'Pos',
      width: 40,
      render: (player) => player?.info?.position || '-'
    },
    {
      key: 'team',
      title: 'Team',
      width: 45,
      render: (player) => player?.info?.mlb_team || '-'
    }
  );

  // Dynamic stat columns based on league categories
  const statColumns = [];
  const cats = viewMode === 'hitting' 
    ? settings.hitting_categories 
    : settings.pitching_categories;

  // Helper function to get stats based on selected year
  // Finds the year object in stats.years array matching targetYear
  // Falls back to most recent year if targetYear not found
  const getStats = (player) => {
    const years = player?.stats?.years || [];

    // First try exact match on targetYear
    let yearObj = years.find(y => y.season === targetYear);

    // If not found and we're looking for "current", try the most recent year in the data
    if (!yearObj && selectedYear === 'current' && years.length > 0) {
      // Sort descending and take first (most recent)
      const sortedYears = [...years].sort((a, b) => (b.season || 0) - (a.season || 0));
      yearObj = sortedYears[0];
    }

    // If still not found and we're looking for "previous", try second most recent
    if (!yearObj && selectedYear === 'previous' && years.length > 1) {
      const sortedYears = [...years].sort((a, b) => (b.season || 0) - (a.season || 0));
      yearObj = sortedYears[1];
    }

    if (!yearObj) return {};

    // Return object with hitting and pitching (matching old stats_data structure)
    return {
      hitting: yearObj.hitting || {},
      pitching: yearObj.pitching || {}
    };
  };

  // Always add AB for hitters, IP/GS for pitchers first
  if (viewMode === 'hitting') {
    statColumns.push({
      key: `at_bats${statSuffix}`,
      title: 'AB',
      width: 40,
      render: (player) => {
        const stats = getStats(player);
        const hitting = stats.hitting || {};
        return renderDefault(hitting.at_bats || 0);
      }
    });
  } else {
    statColumns.push({
      key: `games_started${statSuffix}`,
      title: 'GS',
      width: 35,
      render: (player) => {
        const stats = getStats(player);
        const pitching = stats.pitching || {};
        return renderDefault(pitching.games_started || 0);
      }
    });
    statColumns.push({
      key: `innings_pitched${statSuffix}`,
      title: 'IP',
      width: 40,
      render: (player) => {
        const stats = getStats(player);
        const pitching = stats.pitching || {};
        return renderFloat1(pitching.innings_pitched || 0);
      }
    });
  }

  // Dynamic category columns.
  // DD-STAT-CONSOLIDATION: field-level dedup via the shared helper (one dedup
  // implementation). The static block above always renders AB (hitting) and GS/IP
  // (pitching); drop those so a league that scores them doesn't render a duplicate.
  const staticFields = viewMode === 'hitting' ? ['at_bats'] : ['games_started', 'innings_pitched'];
  (cats || []).filter(c => {
    const cfg = STAT_FIELD_MAP[String(c).toUpperCase()];
    return !(cfg && staticFields.includes(statFieldSimple(cfg)));
  }).forEach(cat => {
    const catUpper = cat.toUpperCase();
    const catLower = cat.toLowerCase();
    let title = catUpper;
    let width = 40;
    let render;
    let key;

    const statMapping = {
      'R': 'runs',
      'RBI': 'rbi',
      'HR': 'home_runs',
      'SB': 'stolen_bases',
      'AVG': 'batting_avg',
      'OBP': 'obp',
      'SLG': 'slg',
      'OPS': 'ops',
      'W': 'wins',
      'L': 'losses',
      'SV': 'saves',
      'S': 'saves',
      'ERA': 'era',
      'WHIP': 'whip',
      'K': 'strikeouts_pitched',
      'QS': 'quality_starts'
    };

    key = statMapping[catUpper] || catLower;

    if (['AVG', 'OBP', 'SLG', 'OPS', 'ERA', 'WHIP'].includes(catUpper)) {
      width = 45;
    }

    // Create render function that accesses canonical structure
    const statKey = `${key}${statSuffix}`;
    const isHitting = viewMode === 'hitting';

    if (['AVG', 'OBP', 'SLG', 'OPS'].includes(catUpper)) {
      render = (player) => {
        const stats = getStats(player);
        const playerStats = isHitting ? (stats.hitting || {}) : (stats.pitching || {});
        // Don't use || 0 - let renderAvg handle null/undefined properly
        return renderAvg(playerStats[key]);
      };
    } else if (['ERA'].includes(catUpper)) {
      render = (player) => {
        const stats = getStats(player);
        const pitching = stats.pitching || {};
        return renderFloat2(pitching[key] || 0);
      };
    } else if (['WHIP'].includes(catUpper)) {
      render = (player) => {
        const stats = getStats(player);
        const pitching = stats.pitching || {};
        return renderFloat3(pitching[key] || 0);
      };
    } else {
      render = (player) => {
        const stats = getStats(player);
        const playerStats = isHitting ? (stats.hitting || {}) : (stats.pitching || {});
        return renderDefault(playerStats[key] || 0);
      };
    }

    statColumns.push({ key: statKey, title, width, render });
  });

  const endColumns = [
    {
      key: 'baseline_price',
      title: 'Generated',
      width: 55,
      render: (player) => {
        const pr = player.pricing || {};
        const priceValue = pr._originalPrice || pr.baseline_price || 0;
        return (
          <span className={pr.manual_price ? dynastyTheme.classes.text.neutralLight : dynastyTheme.classes.text.success}>
            ${renderDefault(priceValue)}
          </span>
        );
      }
    },
    {
      key: 'tier',
      title: 'Tier',
      width: 70,
      render: (player) => {
        const tierColors = {
          elite: 'text-red-400 font-bold',
          star: 'text-orange-400 font-semibold',
          solid: 'text-blue-400',
          regular: 'text-green-400',
          replacement: dynastyTheme.classes.text.neutralLight,
          rookie: 'text-yellow-400',
          unknown: dynastyTheme.classes.text.neutralLight
        };
        const tier = player.pricing?.tier || 'unknown';
        const tierLower = tier.toLowerCase();
        // Capitalize first letter for display
        const displayTier = tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase();
        return <span className={tierColors[tierLower] || dynastyTheme.classes.text.neutralLight}>{displayTier}</span>;
      }
    },
    {
      key: 'impact_score',
      title: 'Impact',
      width: 55,
      render: (player) => renderFloat2(player.pricing?.impact_score)
    },
    {
      key: 'manual_price',
      title: 'Final',
      width: 85,
      render: (player) => {
        const pr = player.pricing || {};
        const playerId = player?.ids?.mlb;
        return (
          <div className="flex items-center gap-1">
            <span className={dynastyTheme.classes.text.success}>$</span>
            <input
              type="number"
              min={settings.min_salary}
              step={settings.salary_increment}
              value={pr.baseline_price || settings.min_salary}
              onChange={(e) => handleManualPriceChange(playerId, e.target.value)}
              className={`${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.border.neutral} ${dynastyTheme.classes.text.white} w-14 px-1 py-0 text-xs rounded border`}
              style={{ height: '22px' }}
              disabled={bulkEditMode}
            />
            {pr.manual_price && pr._originalPrice && (
              <button
                onClick={() => handleManualPriceChange(playerId, pr._originalPrice)}
                className={`text-xs ${dynastyTheme.classes.text.neutralLight} hover:text-gray-300 transition-colors`}
                title="Reset to original"
                style={{ fontSize: '10px' }}
              >
                ↺
              </button>
            )}
          </div>
        );
      }
    }
  ];

  return [...baseColumns, ...statColumns, ...endColumns];
};
